import { isIos } from "../mobile-detect/mobile-detect";

const DEFAULT_DEBOUNCE_TIMEOUT = 250;
let debounceTimeout: null | ReturnType<typeof setTimeout> = null;

export const addClass = (elm: HTMLElement, className: string[]) => elm.classList.add(...className);

export const convertObjectKeysToArray = (obj) => Object.keys(obj).map((data) => [data, obj[data]]);

export const convertObjectValuesToArray = (obj) => Object.values(obj).map((data: any) => [data, obj[data]]);

export const removeClass = (elm: HTMLElement, className: string[]) => elm.classList.remove(...className);

export const setStateClass = (elm, possibleStateClasses, stateClassToAdd) => {
  possibleStateClasses.forEach((stateClass) => removeClass(elm, stateClass));
  addClass(elm, stateClassToAdd);
};

export const toggleClass = (elm, className, shouldAddClass) => elm.classList.toggle(className, shouldAddClass);

export const setStylesOnElement = (styles, element) => Object.assign(element.style, styles);

export const insertAfter = (referenceNode, elmToAppend) => referenceNode.parentNode.insertBefore(elmToAppend, referenceNode.nextSibling);

export const findElementPosition = (elm) => {
  const rect = elm.getBoundingClientRect();
  const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

  const coords = {
    top: rect.top + scrollTop,
    left: rect.left + scrollLeft,
  };

  return coords;
};

window.dataLayer = window.dataLayer || [];
export function gtag(event: any, action: any, params?: any): void {
  if (typeof action === 'string') {
    window.dataLayer.push(arguments);
  } else {
    window.dataLayer.push({ event, ...action });
  }
}

export const queryStringFromObject = (params) =>
  Object.keys(params)
    .map((key) => `${key}=${params[key]}`)
    .join("&");

const format = (k, v) => (v !== null ? `${k}=${encodeURIComponent(v)}` : "");
export const queryStringFromObject2 = (obj) => {
  return []
    .concat(...Object.entries(obj).map(([k, v]) => (Array.isArray(v) ? v.map((arr) => queryStringFromObject2({ [k]: arr })) : format(k, v))))
    .filter((x) => x)
    .join("&");
};

export const waitForFrame = () => new Promise((resolve) => requestAnimationFrame(resolve));
export const checkForModuleToLoad = (elm) => elm.querySelector(".pageJS");
export const getModuleToLoad = (elm) => elm.querySelector(".pageJS").getAttribute("module");
export const getSrcToLoad = (elm) => elm.getAttribute("src");
export const bindModuleToLoad = (module) => window[module].init.bind(window)();
export const unbindModuleToLoade = (module) => window[module].exit.bind(window)();

export const appendScript = (src) => {
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    document.body.appendChild(script);
    script.onload = resolve;
    script.onerror = reject;
    script.async = true;
    script.src = src;
    script.classList.add("js-appended-script");
  });
};

export const removeAppendedScript = (selector = ".js-appended-script") => {
  const scripts = document.querySelectorAll(selector);
  scripts.forEach((script) => {
    script.remove();
  });
};

export const debounce = (func: Function, wait: number = DEFAULT_DEBOUNCE_TIMEOUT) => {
  let timeout: NodeJS.Timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };

    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

export const siteId = () => {
  const body = document.querySelector<HTMLElement>('body');
  return body.dataset.country;
}

export const findAncestor = (el, cls) => {
  while ((el = el.parentElement) && !el.classList.contains(cls));
  return el;
};

export const getParams = (query) => {
  if (!query) {
    return {};
  }
  return (/^[?#]/.test(query) ? query.slice(1) : query).split("&").reduce((params, param) => {
    let [key, value] = param.split("=");
    params[key] = value ? decodeURIComponent(value.replace(/\+/g, " ")) : "";
    return params;
  }, {});
};

export const serialize = (obj) => {
  return (
    "?" +
    Object.entries(obj)
      .map(([key, val]) => `${key}=${val}`)
      .join("&")
  );
};

export const getOffset = (el) => {
  let _x = 0,
    _y = 0;
  while (el && !isNaN(el.offsetLeft) && !isNaN(el.offsetTop)) {
    _x += el.offsetLeft - el.scrollLeft;
    _y += el.offsetTop - el.scrollTop;
    el = el.offsetParent;
  }
  return { top: _y, left: _x } as ScrollToOptions;
};

export const getOffsetWithinParent = (child: HTMLElement, parent: HTMLElement): ScrollToOptions => {
  let offset = 0;
  let el: HTMLElement = child;

  while (el && el !== parent) {
    offset += el.offsetTop;
    el = el.offsetParent as HTMLElement;
  }
  return { top: offset, left: child.offsetLeft };
}

export const scrollTo = (offset: ScrollToOptions, callback = null) => {
  scrollToOnContainer(offset, window, callback);
};

// do you need to scroll to an element that is with an overflow container?
// I.E a div with a scrollbar? or a mobile menu ?
// this function will help with that
// make sure you find the right container for your element
// use utility-functions.getOffset() to get the top and left of the element
export const scrollToOnContainer = (offset: ScrollToOptions, container: HTMLElement | Window = window, callback: Function = null) => {
  var defaultOptions = {
    top: 0,
    left: 0,
    behavior: "smooth",
  };
  const offsetOptions = Object.assign(defaultOptions, offset);
  const onScroll = function () {
    const scrollTop = window.scrollY || window.pageYOffset;
    if (scrollTop === offsetOptions.top) {
      container.removeEventListener("scroll", onScroll);
      if (callback) callback();
    }
  };
  container.addEventListener("scroll", onScroll);
  onScroll();
  container.scrollTo(offsetOptions);
};

export const getAbsoluteHeight = (element) => {
  const elm = typeof element === "string" ? document.querySelector(element) : element;

  const styles = window.getComputedStyle(elm);
  const margin = parseFloat(styles["marginTop"]) + parseFloat(styles["marginBottom"]);

  return Math.ceil(elm.offsetHeight + margin);
};

export const getAntiForgeryToken = () => {
  const tokenElm: HTMLInputElement = document.querySelector('[name="__RequestVerificationToken"]');

  if (!tokenElm) return "";

  return tokenElm.value;
};

const PASSWORD_TOGGLE_DELEGATION_KEY = "__rcmPasswordToggleDelegation";

const onPasswordToggleClick = (e: MouseEvent) => {
  const raw = e.target;
  if (!raw || !(raw instanceof Element)) return;
  const elm = raw.closest<HTMLElement>(".js-toggle-password");
  if (!elm) return;

  e.preventDefault();

  const group = elm.closest(".form-group");
  const input = group?.querySelector<HTMLInputElement>(".js-toggle-password-field");
  if (!input) return;

  if (elm.dataset.toggle === "show") {
    elm.setAttribute("data-toggle", "hide");
    input.setAttribute("type", "text");
  } else if (elm.dataset.toggle === "hide") {
    elm.setAttribute("data-toggle", "show");
    input.setAttribute("type", "password");
  }
};

export const togglePasswordVisibility = () => {
  const win = window as Window & Record<string, boolean | undefined>;
  if (win[PASSWORD_TOGGLE_DELEGATION_KEY]) return;
  win[PASSWORD_TOGGLE_DELEGATION_KEY] = true;

  document.addEventListener("click", onPasswordToggleClick, false);
};

export const dispatchCustomEventWrapper = (el: HTMLElement | Document, name: string, data: LooseObject) => {
  const evt = new CustomEvent(name, {
    detail: data,
    bubbles: true,
  });
  el.dispatchEvent(evt);
};

// Helper: check if element (or any parent) is scrollable
const isScrollable = (el: HTMLElement | null) => {
    while (el && el !== document.documentElement) {
        const style = getComputedStyle(el);
        const overflowY = style.overflowY;
        if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight) {
            return true;
        }
        el = el.parentElement;
    }
    return false;
};

// Store scroll position
let scrollPosition = 0;

// function to disable scroll on the page
// use this function to prevent the page from scrolling when you have a modal open
// this is used on search modal open when its open in mobile view
export const disableScroll = (): void => {
    const win = window as any;
    const html = document.documentElement;

    // Initialise or increment global scroll lock counter
    if (typeof win._scrollLockCount === 'number' && win._scrollLockCount > 0) {
        win._scrollLockCount += 1;
        return;
    }

    win._scrollLockCount = 1;

    // store exact scroll position
    scrollPosition = window.scrollY;

    // set CSS variable first
    html.style.setProperty('--scroll-top', `-${scrollPosition}px`);

    // force a layout flush to stabilise layout
    void html.offsetHeight;

    // add scroll lock class
    html.classList.add('disable-scroll');

    // iOS touch lock
    if (isIos()) document.ontouchmove = (e) => e.preventDefault();

    // allow scrolling inside panels
    const scrollHandler = (e: Event) => {
        if (isScrollable(e.target as HTMLElement)) return;
        e.preventDefault();
    };
    html.addEventListener('wheel', scrollHandler, { passive: false });
    html.addEventListener('touchmove', scrollHandler, { passive: false });

    win._scrollLockHandler = scrollHandler;
};


// function to enable scroll on the page
// use this function to re-enable the page to scroll when you have closed a modal
// this is used on search modal close when its open in mobile view
export const enableScroll = (): void => {
    const win = window as any;
    const html = document.documentElement;

    const currentCount = typeof win._scrollLockCount === 'number' ? win._scrollLockCount : 0;

    // More than one lock active: just decrement and keep scroll disabled
    if (currentCount > 1) {
        win._scrollLockCount = currentCount - 1;
        return;
    }

    // No active locks tracked and no class on html – nothing to do
    if (currentCount <= 0 && !html.classList.contains('disable-scroll')) {
        win._scrollLockCount = 0;
        return;
    }

    // At this point we are releasing the final lock
    win._scrollLockCount = 0;

    // Use --scroll-top as source of truth (it holds -scrollY). Restore before unlocking so we don't jump to top.
    const scrollTopVar = getComputedStyle(html).getPropertyValue('--scroll-top').trim();
    const positionToRestore = scrollTopVar ? Math.abs(parseFloat(scrollTopVar)) || scrollPosition : scrollPosition;

    // Restore scroll position before removing the class so the viewport is correct when layout updates
    window.scrollTo(0, positionToRestore);

    // keep transitions disabled slightly longer
    html.classList.add('scroll-restoring');

    // remove wheel/touch blockers
    const scrollHandler = win._scrollLockHandler;
    if (scrollHandler) {
        html.removeEventListener('wheel', scrollHandler);
        html.removeEventListener('touchmove', scrollHandler);
        delete win._scrollLockHandler;
    }

    // iOS touch unlock
    if (isIos()) document.ontouchmove = () => true;

    // remove scroll lock
    html.classList.remove('disable-scroll');
    html.style.removeProperty('--scroll-top');

    // Backup restore and re-enable transitions on next frame
    requestAnimationFrame(() => {
        window.scrollTo(0, positionToRestore);
        requestAnimationFrame(() => html.classList.remove('scroll-restoring'));
    });
};


/**
 * Initializes country selection redirect functionality
 * Attaches an event listener to the country selection dropdown
 * that will redirect the user to the appropriate site when changed
 *
 * @returns {void} No return value
 */
export const checkForRedirect = (): void => {
  const countrySelect = document.querySelector<HTMLSelectElement>('.js-country-check');
  // return if no country select is found
  if (!countrySelect) return;

  countrySelect.addEventListener('change',
    (event) => processCountrySelection(event.currentTarget as HTMLSelectElement));
}

/**
 * Processes the country selection and redirects if necessary
 * Makes an API call to check if redirection is needed based on the selected country
 *
 * @param {HTMLSelectElement} el - The country selection dropdown element
 * @returns {Promise<void>} A promise that resolves when the process completes
 */
export const processCountrySelection = async (el: HTMLSelectElement): Promise<void> => {
  // Validate input
  if (!el || !el.options || el.selectedIndex < 0) {
    console.error('Invalid select element or no option selected');
    return;
  }

  const selectedOption = el.options[el.selectedIndex];
  const selectedValue = selectedOption.value;

  if (selectedValue === '15' || selectedOption.text.trim().toLowerCase() === 'australia') {
    window.location.href = 'https://mossmenswear.com.au/';
    return;
  }

  // Set loading state
  el.disabled = true;

  try {
    const url = new URL('/basket/basket/checkcountryredirect', window.location.origin);
    url.searchParams.append('countryid', selectedValue);

    const response = await fetch(url.toString());

    // Check if response is ok
    if (!response.ok) {
      console.error(`HTTP error occurred: Status ${response.status}`);
      return;
    }

    interface ResponseData {
      redirect: boolean;
      redirectUrl: string;
    }

    const responseData: ResponseData = await response.json();

    // Validate redirect data before redirecting
    if (responseData.redirect === true && responseData.redirectUrl) {
      window.location.href = responseData.redirectUrl;
    }
  } catch (error) {
    console.error('Error checking country redirect:', error);
  } finally {
    el.disabled = false;
  }
}

export let outfitbuilder: any = {};
